import { calendarMailboxLinksUrl } from '../core/calendar-reconcile-contract.js';
import type { CalendarReconcileContext } from './calendar-view-types.js';

export interface CalendarAreaRef {
  kind: 'public' | 'teamspace' | 'private' | 'app';
  name: string;
  ownerUserId: string | null;
  rootFolderId: string;
  systemFolderId: string | null;
  workspaceId: string;
}

export type CalendarMailboxLinkVisibility = 'full' | 'hidden' | 'redacted';

type FetchLike = typeof fetch;

export type CalendarMailboxLink = {
  canConfigure: boolean;
  canSend: boolean;
  canSync: boolean;
  id: string;
  inboundEnabled: boolean;
  mailAccountId: string;
  mailbox: CalendarMailboxLinkMailbox | null;
  mailboxVisibility: 'full' | 'hidden' | 'redacted';
  operationPolicies: CalendarMailboxLinkOperationPolicies;
  outboundEnabled: boolean;
  policy: CalendarMailboxLinkPolicy;
  policyReasons: CalendarMailboxLinkReason[];
  status: 'active' | 'disabled';
  syncState?: 'failed' | 'local' | 'pending' | 'synced';
};

export type CalendarMailboxLinkReason =
  | 'allowed'
  | 'area_mismatch'
  | 'credential_unavailable'
  | 'missing_capability'
  | 'resource_hidden'
  | 'resource_unavailable';

export type CalendarMailboxLinkPolicy = {
  allowed: boolean;
  reasons: CalendarMailboxLinkReason[];
};

export type CalendarMailboxLinkOperationPolicies = {
  configure: CalendarMailboxLinkPolicy;
  send: CalendarMailboxLinkPolicy | null;
  sync: CalendarMailboxLinkPolicy | null;
};

export type CalendarMailboxLinkMailbox = {
  area: CalendarAreaRef | null;
  missingCapabilities: Array<
    | 'manage'
    | 'provider_connect'
    | 'provider_send'
    | 'provider_sync'
    | 'read'
    | 'write'
  >;
  reasons: CalendarMailboxLinkReason[];
  title: string | null;
  visibility: 'full' | 'hidden' | 'redacted';
};

export type CalendarMailboxLinksResponse = {
  decisions: CalendarMailboxLinkDecision[];
  links: CalendarMailboxLink[];
};

export type CalendarMailboxLinkDecision = {
  canConfigure: boolean;
  canSend: boolean;
  canSync: boolean;
  inboundEnabled: boolean;
  mailAccountId: string;
  mailbox: CalendarMailboxLinkMailbox | null;
  mailboxVisibility: 'full' | 'hidden' | 'redacted';
  mode: 'both' | 'inbound' | 'outbound';
  operationPolicies: CalendarMailboxLinkOperationPolicies;
  outboundEnabled: boolean;
  policy: CalendarMailboxLinkPolicy;
  policyReasons: CalendarMailboxLinkReason[];
};

export type CalendarMailboxSendEligibility = {
  canSend: boolean;
  hasOutboundLink: boolean;
  reason: CalendarMailboxLinkReason | 'load_failed' | 'not_linked' | null;
};

export type CalendarMailboxLinkCreateInput = {
  inboundEnabled?: boolean;
  mailAccountId: string;
  outboundEnabled?: boolean;
};

export type CalendarMailboxLinkPatch = {
  inboundEnabled?: boolean;
  outboundEnabled?: boolean;
  status?: 'active' | 'disabled';
};

type CalendarMailboxContext = Pick<
  CalendarReconcileContext,
  'databaseId' | 'docId' | 'viewId' | 'workspaceId'
>;

export class CalendarMailboxLinksRequestError extends Error {
  constructor(readonly status: number) {
    super(`Calendar mailbox links could not be loaded: ${status}`);
    this.name = 'CalendarMailboxLinksRequestError';
  }
}

export function isTransientCalendarMailboxLinksError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (!(error instanceof CalendarMailboxLinksRequestError)) return false;
  return (
    error.status === 408 ||
    error.status === 425 ||
    error.status === 429 ||
    error.status >= 500
  );
}

// A view may remount while offline. Keep the last owning backend decision
// with its live workspace, not with a transient UI element. Actor and source
// are part of the key; no policy crosses an account or workspace lifetime.
const mailboxPolicies = new WeakMap<
  object,
  Map<string, CalendarMailboxLinksResponse>
>();

export async function loadCalendarMailboxLinks(
  workspace: object,
  context: CalendarMailboxContext & { actorUserId: string },
  fetcher: FetchLike = fetch
): Promise<CalendarMailboxLinksResponse> {
  let policies = mailboxPolicies.get(workspace);
  if (!policies) {
    policies = new Map();
    mailboxPolicies.set(workspace, policies);
  }
  const key = JSON.stringify([
    context.actorUserId,
    calendarMailboxLinksUrl(context),
  ]);
  try {
    const result = await fetchCalendarMailboxLinks(context, fetcher);
    policies.set(key, result);
    return result;
  } catch (error) {
    const previous = policies.get(key);
    if (isTransientCalendarMailboxLinksError(error) && previous)
      return previous;
    policies.delete(key);
    throw error;
  }
}

export async function fetchCalendarMailboxLinks(
  context: CalendarMailboxContext,
  fetcher: FetchLike = fetch
): Promise<CalendarMailboxLinksResponse> {
  const response = await fetcher(calendarMailboxLinksUrl(context), {
    credentials: 'include',
    method: 'GET',
  });
  if (!response.ok) {
    throw new CalendarMailboxLinksRequestError(response.status);
  }
  const payload =
    (await response.json()) as Partial<CalendarMailboxLinksResponse>;
  return {
    decisions: Array.isArray(payload.decisions) ? payload.decisions : [],
    links: Array.isArray(payload.links)
      ? payload.links.map(normalizeCalendarMailboxLink)
      : [],
  };
}

export function calendarMailboxLinkUrl(
  context: CalendarMailboxContext,
  linkId: string
): string {
  return `${calendarMailboxLinksUrl(context)}/${encodeURIComponent(linkId)}`;
}

export async function createCalendarMailboxLink(
  context: CalendarMailboxContext,
  input: CalendarMailboxLinkCreateInput,
  fetcher: FetchLike = fetch
): Promise<CalendarMailboxLink> {
  return await writeCalendarMailboxLink(
    calendarMailboxLinksUrl(context),
    'POST',
    input,
    fetcher
  );
}

export async function updateCalendarMailboxLink(
  context: CalendarMailboxContext,
  linkId: string,
  patch: CalendarMailboxLinkPatch,
  fetcher: FetchLike = fetch
): Promise<CalendarMailboxLink> {
  return await writeCalendarMailboxLink(
    calendarMailboxLinkUrl(context, linkId),
    'PATCH',
    patch,
    fetcher
  );
}

export async function deleteCalendarMailboxLink(
  context: CalendarMailboxContext,
  linkId: string,
  fetcher: FetchLike = fetch
): Promise<boolean> {
  const response = await fetcher(calendarMailboxLinkUrl(context, linkId), {
    credentials: 'include',
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(
      `Calendar mailbox link could not be deleted: ${response.status}`
    );
  }
  const payload = (await response.json()) as { deleted?: boolean };
  return payload.deleted === true;
}

export function hasActiveOutboundMailboxLink(
  links: readonly CalendarMailboxLink[]
): boolean {
  return links.some(
    link => link.status === 'active' && link.outboundEnabled === true
  );
}

export function calendarMailboxSendEligibility(
  links: readonly CalendarMailboxLink[],
  loadFailed = false
): CalendarMailboxSendEligibility {
  const outboundLinks = links.filter(
    link => link.status === 'active' && link.outboundEnabled === true
  );
  if (loadFailed) {
    return {
      canSend: false,
      hasOutboundLink: outboundLinks.length > 0,
      reason: 'load_failed',
    };
  }
  if (outboundLinks.length === 0) {
    return { canSend: false, hasOutboundLink: false, reason: 'not_linked' };
  }
  const allowed = outboundLinks.find(
    link =>
      link.canSend === true &&
      calendarMailboxOperationPolicy(link, 'send')?.allowed === true
  );
  if (allowed) {
    return { canSend: true, hasOutboundLink: true, reason: null };
  }
  return {
    canSend: false,
    hasOutboundLink: true,
    reason:
      outboundLinks
        .flatMap(link => {
          const sendPolicy = calendarMailboxOperationPolicy(link, 'send');
          return (
            sendPolicy?.reasons ??
            (['resource_unavailable'] satisfies CalendarMailboxLinkReason[])
          );
        })
        .find(reason => reason !== 'allowed') ?? 'resource_unavailable',
  };
}

function normalizeCalendarMailboxLink(
  link: CalendarMailboxLink
): CalendarMailboxLink {
  const policy = isCalendarMailboxPolicy(link.policy)
    ? link.policy
    : failClosedMailboxPolicy();
  const operationPolicies = normalizeCalendarMailboxOperationPolicies(
    link.operationPolicies
  );
  const mailboxVisibility = link.mailbox?.visibility ?? link.mailboxVisibility;
  return {
    ...link,
    canConfigure:
      link.canConfigure === true &&
      operationPolicies.configure.allowed === true,
    canSend: link.canSend === true && operationPolicies.send?.allowed === true,
    canSync: link.canSync === true && operationPolicies.sync?.allowed === true,
    mailbox: link.mailbox ?? null,
    mailboxVisibility: isCalendarMailboxVisibility(mailboxVisibility)
      ? mailboxVisibility
      : 'hidden',
    operationPolicies,
    policy,
    policyReasons: Array.isArray(link.policyReasons)
      ? link.policyReasons
      : [...policy.reasons],
  };
}

function calendarMailboxOperationPolicy(
  link: CalendarMailboxLink,
  operation: keyof CalendarMailboxLinkOperationPolicies
): CalendarMailboxLinkPolicy | null {
  return normalizeCalendarMailboxOperationPolicies(link.operationPolicies)[
    operation
  ];
}

function normalizeCalendarMailboxOperationPolicies(
  policies: unknown
): CalendarMailboxLinkOperationPolicies {
  if (!policies || typeof policies !== 'object') {
    return failClosedMailboxOperationPolicies();
  }
  const candidate = policies as Partial<CalendarMailboxLinkOperationPolicies>;
  return {
    configure: isCalendarMailboxPolicy(candidate.configure)
      ? candidate.configure
      : failClosedMailboxPolicy(),
    send:
      candidate.send === null
        ? null
        : isCalendarMailboxPolicy(candidate.send)
          ? candidate.send
          : failClosedMailboxPolicy(),
    sync:
      candidate.sync === null
        ? null
        : isCalendarMailboxPolicy(candidate.sync)
          ? candidate.sync
          : failClosedMailboxPolicy(),
  };
}

function failClosedMailboxOperationPolicies(): CalendarMailboxLinkOperationPolicies {
  return {
    configure: failClosedMailboxPolicy(),
    send: failClosedMailboxPolicy(),
    sync: failClosedMailboxPolicy(),
  };
}

function failClosedMailboxPolicy(): CalendarMailboxLinkPolicy {
  return {
    allowed: false,
    reasons: ['resource_unavailable'],
  };
}

function isCalendarMailboxPolicy(
  policy: unknown
): policy is CalendarMailboxLinkPolicy {
  if (!policy || typeof policy !== 'object') return false;
  const candidate = policy as Partial<CalendarMailboxLinkPolicy>;
  return (
    typeof candidate.allowed === 'boolean' && Array.isArray(candidate.reasons)
  );
}

function isCalendarMailboxVisibility(
  value: unknown
): value is CalendarMailboxLink['mailboxVisibility'] {
  return value === 'full' || value === 'hidden' || value === 'redacted';
}

async function writeCalendarMailboxLink(
  url: string,
  method: 'PATCH' | 'POST',
  body: CalendarMailboxLinkCreateInput | CalendarMailboxLinkPatch,
  fetcher: FetchLike
): Promise<CalendarMailboxLink> {
  const response = await fetcher(url, {
    body: JSON.stringify(body),
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
    },
    method,
  });
  if (!response.ok) {
    throw new Error(
      `Calendar mailbox link could not be written: ${response.status}`
    );
  }
  return normalizeCalendarMailboxLink(
    (await response.json()) as CalendarMailboxLink
  );
}
